import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import {
  MaterialOptionProps,
  PartyOptionProps,
} from "../../types/materialLine";
import {
  CuttingOrderDateBy,
  CuttingOrderListProps,
  CuttingOrderProps,
  CuttingOrderSortBy,
  CuttingOrderSortOrder,
  CuttingOrderState,
  CuttingPartyInfoProps,
} from "../../types/cuttingOrder";

export interface CuttingOrdersState {
  orders: CuttingOrderListProps[];
  totalOrders: number;
  currentOrder: CuttingOrderProps | null;
  currentOrderLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  materials: MaterialOptionProps[];
  materialsLoading: boolean;
  partyInfo: CuttingPartyInfoProps;
  partyInfoLoading: boolean;
  partyInfoRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const emptyPartyInfo: CuttingPartyInfoProps = {
  inhouse: false,
  options: [],
  stock: [],
  scrap_below: null,
  later: false,
};

const initialState: CuttingOrdersState = {
  orders: [],
  totalOrders: 0,
  currentOrder: null,
  currentOrderLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  materials: [],
  materialsLoading: false,
  partyInfo: emptyPartyInfo,
  partyInfoLoading: false,
  partyInfoRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchCuttingOrderListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  status?: CuttingOrderState | null;
  date_by?: CuttingOrderDateBy;
  sort_by?: CuttingOrderSortBy;
  sort_order?: CuttingOrderSortOrder;
};

export const fetchCuttingOrderListData = createAsyncThunk(
  "fetchCuttingOrderListData",
  async (req: FetchCuttingOrderListDataProps) => {
    let response: any;
    await GET(`/v1/admin/cutting-orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchCuttingOrderDetail = createAsyncThunk(
  "fetchCuttingOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/cutting-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// Loads the open order again without clearing it first, so the page keeps showing it (no loader), e.g. after one of its
// receives was deleted.
export const refreshCuttingOrderDetail = createAsyncThunk(
  "refreshCuttingOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/cutting-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// partyId keeps an edited order's own party in the list.
export const fetchCuttingOrderParties = createAsyncThunk(
  "fetchCuttingOrderParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/cutting-orders/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every cutting party, active or not, and every party that has orders.
export const fetchCuttingOrderFilterParties = createAsyncThunk(
  "fetchCuttingOrderFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/cutting-orders/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// Every material with its bar sizes, for the bars sent with an order.
export const fetchCuttingOrderMaterials = createAsyncThunk(
  "fetchCuttingOrderMaterials",
  async () => {
    let response: any;
    await GET(`/v1/admin/cutting-orders/materials`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The items the party can be asked to cut and its material balance; order_id leaves out the order being edited.
export const fetchCuttingOrderPartyInfo = createAsyncThunk(
  "fetchCuttingOrderPartyInfo",
  async (req: { party_id: number; order_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/cutting-orders/party-info`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const cuttingOrdersSlice = createSlice({
  name: "cuttingOrders",
  initialState,
  reducers: {
    clearCurrentCuttingOrder: (state) => {
      state.currentOrder = null;
      state.currentOrderLoading = false;
    },
    clearCuttingOrderPartyInfo: (state) => {
      state.partyInfo = emptyPartyInfo;
      state.partyInfoLoading = false;
      state.partyInfoRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCuttingOrderListData.pending, (state) => {
      state.orders = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCuttingOrderListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.orders = action.payload.data.data.records;
          state.totalOrders = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.orders = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchCuttingOrderListData.rejected,
      (state, action: any) => {
        state.orders = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchCuttingOrderDetail.pending, (state) => {
      state.currentOrderLoading = true;
      state.currentOrder = null;
    });
    builder.addCase(fetchCuttingOrderDetail.fulfilled, (state, action: any) => {
      state.currentOrderLoading = false;
      if (action.payload.data.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });
    builder.addCase(fetchCuttingOrderDetail.rejected, (state) => {
      state.currentOrderLoading = false;
      state.currentOrder = null;
    });

    builder.addCase(refreshCuttingOrderDetail.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });

    builder.addCase(fetchCuttingOrderParties.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.partyOptions = action.payload.data.data.records.map(
          (party: any) => ({ value: party.id, label: partyText(party) })
        );
      }
    });

    builder.addCase(
      fetchCuttingOrderFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(fetchCuttingOrderMaterials.pending, (state) => {
      state.materialsLoading = true;
    });
    builder.addCase(
      fetchCuttingOrderMaterials.fulfilled,
      (state, action: any) => {
        state.materialsLoading = false;
        if (action.payload.data.status === "success") {
          state.materials = action.payload.data.data.records;
        }
      }
    );
    builder.addCase(fetchCuttingOrderMaterials.rejected, (state) => {
      state.materialsLoading = false;
    });

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchCuttingOrderPartyInfo.pending, (state, action) => {
      state.partyInfo = emptyPartyInfo;
      state.partyInfoLoading = true;
      state.partyInfoRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchCuttingOrderPartyInfo.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.partyInfoRequestId) return;
        state.partyInfoLoading = false;
        state.partyInfo =
          action.payload.data.status === "success"
            ? action.payload.data.data
            : emptyPartyInfo;
      }
    );
    builder.addCase(fetchCuttingOrderPartyInfo.rejected, (state, action) => {
      if (action.meta.requestId !== state.partyInfoRequestId) return;
      state.partyInfoLoading = false;
    });
  },
});

export const { clearCurrentCuttingOrder, clearCuttingOrderPartyInfo } =
  cuttingOrdersSlice.actions;

export const getCuttingOrders = (state: RootState) =>
  state.cuttingOrders.orders;
export const getTotalCuttingOrders = (state: RootState) =>
  state.cuttingOrders.totalOrders;
export const getCurrentCuttingOrder = (state: RootState) =>
  state.cuttingOrders.currentOrder;
export const getCurrentCuttingOrderLoading = (state: RootState) =>
  state.cuttingOrders.currentOrderLoading;
export const getCuttingOrderPartyOptions = (state: RootState) =>
  state.cuttingOrders.partyOptions;
export const getCuttingOrderFilterPartyOptions = (state: RootState) =>
  state.cuttingOrders.filterPartyOptions;
export const getCuttingOrderMaterials = (state: RootState) =>
  state.cuttingOrders.materials;
export const getCuttingOrderMaterialsLoading = (state: RootState) =>
  state.cuttingOrders.materialsLoading;
export const getCuttingOrderPartyInfo = (state: RootState) =>
  state.cuttingOrders.partyInfo;
export const getCuttingOrderPartyInfoLoading = (state: RootState) =>
  state.cuttingOrders.partyInfoLoading;

export default cuttingOrdersSlice.reducer;
